import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StorageType } from '@core/enums';
import { StorageService } from '@core/services/storage.service';
import { User } from '@sentry/angular-ivy';
import { getAuth } from 'firebase/auth';
import { Intercom } from 'ng-intercom';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AmplitudeService } from '../../core/services/amplitude.service';
import { AuthDialogComponent } from '../../routes/auth/auth-dialog/auth-dialog.component';
import { CookieService } from './cookie.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	constructor(
		private intercom: Intercom,
		private amplitude: AmplitudeService,
		private storageService: StorageService,
		private matDialog: MatDialog,
		private cookie: CookieService
	) {}

	private readonly _user$ = new BehaviorSubject<User>(null);
	private readonly _toggleHeaderLogin$ = new BehaviorSubject<boolean>(true);
	private prodorStag: boolean = environment.production;

	get user$(): Observable<User> {
		return this._user$.asObservable();
	}

	get toggleHeaderLogin$(): Observable<boolean> {
		return this._toggleHeaderLogin$.asObservable();
	}

	getUser(): User {
		return this._user$.getValue();
	}

	setUser(user: User): void {
		this._user$.next(user);
	}

	setToggleHeaderLogin(toggleHeaderLogin: boolean): void {
		this._toggleHeaderLogin$.next(toggleHeaderLogin);
	}

	loginV2(user: User): void {
		// Set custom user id for Amplitude
		this.amplitude.setUserId(user.phone)
		this.cookie.set('userToken', user?.token);
		this.storageService.setItem(StorageType.USER, JSON.stringify(user));
		this.setUser(user);
		this.shutdownIntercom();
	}

	// TODO: Remove after the whole AuthService is refactored. As this is affecting the new Auth Flow. And this is not scalable enough.
	login(userID: string, name: string, phone: string, verified: string, token: string, userType: string) {
		// Set custom user id for Amplitude
		this.amplitude.setUserId(phone);

		localStorage.setItem(
			'user',
			JSON.stringify({
				userID: userID,
				name: name,
				phone: phone,
				verified: verified,
				token: token,
				userType: userType,
				phonePrefix: this.user()['phonePrefix'],
			})
		);
		this.shutdownIntercom();
	}

	logout() {
		// Amplitude logout to make user anonymous
		this.amplitude.setUserId(null);

		const LANG = localStorage.getItem('lang');
		localStorage.clear();
		localStorage.setItem('lang', LANG);
        this.cookie.clear('userToken');
		this.shutdownIntercom();
		this.setUser(null);

		// Firebase sign out
		getAuth().signOut();
	}

	user(): any {
		return JSON.parse(localStorage.getItem('user'));
	}

	bootIntercom(hideIntercom = false) {
		if (this.user()) {
			this.intercom.boot({
				user_id: this.user()['userID'],
				name: this.user()['name'],
				email: this.user()['email'],
				widget: {
					activator: '#intercom',
				},
				hide_default_launcher: hideIntercom,
			});
		} else {
			this.intercom.boot({
				widget: {
					activator: '#intercom',
				},
				hide_default_launcher: hideIntercom,
			});
		}
	}

	shutdownIntercom() {
		this.intercom.shutdown();
		this.bootIntercom();
	}

	intercomUpdate(data?: any) {
		this.intercom.update({ adid: data, prodOrStag: this.prodorStag });
	}

	intercomCreateunKnown(data?: any) {
		const visitor = this.intercom.getVisitorId();

		if (visitor) {
			this.intercom.update({ adid: data });
		}
	}

	openAuthDialog(dialogData?: any): void {
		this.matDialog.open(AuthDialogComponent, {
			panelClass: 'auth-modal',
			disableClose: true,
			...(!!dialogData && { data: dialogData }),
		});
	}
}
